import { io } from "socket.io-client";
import { v4 as uuid } from 'uuid';
import config, { turnServerUrl, stunServerUrl } from "../config";
import P2pDataAdapter from "./p2pAdapter";
import VideoStreamQualityManager from "./VideoStreamQualityManager";
import DriftingScore from "./DriftingScore";

const mediasoupClient = require("mediasoup-client");
const ASPECT_RATIO = config.aspectRatios;
const peerToBitrateMapping = config.peerToBitrateMapping;

const WEBCAM_SIMULCAST_ENCODINGS =
  [
    { scaleResolutionDownBy: 4, maxBitrate: 500000 },
    { scaleResolutionDownBy: 2, maxBitrate: 900000 },
    { scaleResolutionDownBy: 1, maxBitrate: 2500000 }
  ];

const peerToWidthMapping = {
  "1": null,
  "2": null,
  "3": null,
  "4": null,
  "5": null,
  "6": null,
  "7": null,
  "8": 400,
  "9": 320,
  "10": 320
}

export default class CommunicationAdapter {
  constructor(backendUrl, room, metadata, producerParams, callback, webrtcType, simulcast, isRecordingPeer, classifier) {
    this.classifier = classifier || "Real";
    this.simulcast = simulcast;
    this.webrtcType = webrtcType || "sfu";
    this.aspectRatio = ASPECT_RATIO;
    this.turnCreds = {
      username: "username",
      password: "password"
    }
    this.botDataProducer = null;
    this.consumerTransport = undefined
    this.producerTransport = undefined
    this.audioTransmitting = false;
    this.reportedTransports = new Set();
    this.filters = {};
    this.brightness = {};
    this.videoElProps = {};
    this.socket = undefined
    this.peerMediaHandler = undefined
    this.room = room;
    this.metadata = metadata;
    this.mediaProducers = [];
    this.mediaConsumers = [];
    this.producerParams = producerParams; // {audio: true, video: true}...
    this.callback = callback || (() => { });
    try {
      this.webRTCDevice = new mediasoupClient.Device();
    } catch (err) {
      console.log(err);
      console.log("error while loading mediasoup client device");
      // alert("Failed to load WebRTCDevice", err);
    }
    this.manager = new VideoStreamQualityManager();
    this.manager.setOnSanitizedVideoStreamQualityIncrease(this.onSanitizedVideoStreamQualityIncrease.bind(this));
    this.manager.setOnSanitizedVideoStreamQualityDecrease(this.onSanitizedVideoStreamQualityDecrease.bind(this));
    this.driftingScore = null;
    this.socket = io(backendUrl, {
      transports: ['websocket'],
      query: { session_id: room },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      maxReconnectionAttempts: 10,
      timeout: 20000,
      forceNew: true
    });
    this.p2pAdapter = new P2pDataAdapter(this.socket, callback, producerParams, ASPECT_RATIO, room);
    // Listen for keydown events
    document.addEventListener('keydown', (event) => {
      var that = this;
      // Check for specific key combinations
      if (event.ctrlKey && event.shiftKey && event.key === 'F') {
        event.preventDefault(); // Prevent default browser behavior
        console.log('Ctrl+Shift+F pressed!');
        // Your callback function here
        that.startFakeDrift();
      }

    });

    this.socket.on("disconnect", (reason) => {
      console.log('socket disconnect:', reason);
      this.callback({
        type: "signalDisconnected",
        data: {
          videos: Object.keys(this.videoElProps).map(x => ({
            constraints: this.videoElProps[x].constraints,
            stream: this.videoElProps[x].videoEl.srcObject,
            type: this.videoElProps[x].type
          }))
        }
      })
    })
    this.socket.on('connect_error', (err) => {
      console.log('socket connect_error:', err);
      this.callback({
        type: "signalDisconnected",
        data: {
          videos: Object.keys(this.videoElProps).map(x => ({
            constraints: this.videoElProps[x].constraints,
            stream: this.videoElProps[x].videoEl.srcObject,
            type: this.videoElProps[x].type
          }))
        }
      })
    });
    this.socket.on('error', (err) => {
      console.log('socket error:', err);
    });

    // Socket.IO 4.x reconnection events
    this.socket.on('reconnect', (attemptNumber) => {
      console.log('socket reconnected after', attemptNumber, 'attempts');
      this.callback({
        type: "signalReconnected",
        data: { attemptNumber }
      });
    });

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log('socket reconnect attempt:', attemptNumber);
    });

    this.socket.on('reconnect_error', (error) => {
      console.log('socket reconnect error:', error);
    });

    this.socket.on('reconnect_failed', () => {
      console.log('socket reconnect failed - max attempts reached');
      this.callback({
        type: "signalReconnectFailed"
      });
    });
    this.socket.on("connect", () => {
      this.socket.emit("initialize", {
        room,
        metadata,
        isRecordingPeer
      })
    })

    this.socket.on("unauthorized", (data) => this.callback({
      type: "unauthorized",
      data: {
        message: data.message
      }
    }))
    this.socket.on("identityAck", this.identityReceived.bind(this))
    this.socket.on("createdTransports", this.createdTransports.bind(this));
    this.socket.on("consumeMedia", this.consumeMedia.bind(this));
    this.socket.on("destroyMedia", this.destroyMedia.bind(this));
    this.socket.on("peerDisconnect", (data) => {
      // this.mediaConsumers.filter(x => x.peerId === data.peerId).map(x => {
      // if (x.isDesktop) {
      // the left peer was sharing video, now we can share the right one
      // this.normalizeOutgoingVideos();
      // }
      // })
      this.mediaConsumers = this.mediaConsumers.filter(x => x.peerId !== data.peerId);
      this.consolidateOutgoingVideoResolutions()
      this.callback({
        type: "disconnect",
        data
      })
    })

    this.socket.on("metaEvent", (data) => this.callback({
      type: data.type,
      data
    }))

    this.socket.on("producerScore", this.producerScoreHandler.bind(this))
    this.socket.on("consumerScaore", this.consumerScoreHandler.bind(this))
    this.socket.on("sessionEnded", (data) => this.callback({
      type: "sessionEnded",
      data
    }));

    this.peerTelemetryReported = new Set(); // To track which peers we've already reported telemetry for
    this.joinSummaryReported = false;
    this.initialPeersCount = 0;
    this.peerSuccessCount = 0;
    this.peerFailureCount = 0;
    this.pendingConsumeRequests = [];
    this.allConsumersPaused = false;
  }

  _reportJoinTelemetry(othersOnJoin, sessionAgeOnJoin) {
    console.log("Reporting join telemetry", { othersOnJoin, sessionAgeOnJoin, room: this.room, classifier: this.classifier });
    this.socket.emit("joinTelemetry", {
      others_on_join: othersOnJoin,
      session_age_on_join: sessionAgeOnJoin,
      room: this.room,
      classifier: this.classifier
    });
  }

  _reportPeerTelemetry(peerId, conclusion) {
    if (this.peerTelemetryReported.has(peerId)) return;
    this.peerTelemetryReported.add(peerId);

    console.log(`[WebRTC-Tele] Reporting peer telemetry for ${peerId}`, conclusion);
    this.socket.emit("peerTelemetry", {
      video_seen: conclusion.status === 'established',
      relay: conclusion.turn || false,
      p2p: conclusion.p2p || false,
      turn_server: conclusion.turn || false,
      protocol: conclusion.protocol || 'unknown',
      user_agent: navigator.userAgent,
      local_candidate_type: conclusion.localCandidateType || 'unknown',
      remote_candidate_type: conclusion.remoteCandidateType || 'unknown',
      remote_ip: conclusion.remoteIp || 'unknown',
      ice_state: conclusion.iceState || 'unknown',
      local_candidate_url: conclusion.localUrl || '',
      local_candidate_port: conclusion.localPort,
      local_candidate_related_port: conclusion.localRelatedPort,
      local_candidate_related_address: conclusion.localRelatedAddress,
      rtt: conclusion.rtt,
      classifier: this.classifier
    });

    if (conclusion.status === 'established') {
      this.peerSuccessCount++;
    } else {
      this.peerFailureCount++;
    }

    this._checkAndReportJoinSummary();
  }

  _setupPeerTelemetryTimeout(peerId, path) {
    setTimeout(() => {
      if (!this.peerTelemetryReported.has(peerId)) {
        console.log(`[WebRTC-Tele] Peer telemetry timeout for ${peerId} (${path}). Reporting failure.`);
        this._reportPeerTelemetry(peerId, {
          transport: path,
          status: 'timeout'
        });
      }
    }, 90000);
  }

  _checkAndReportJoinSummary() {
    if (this.joinSummaryReported) return;

    const reportedSoFar = this.peerSuccessCount + this.peerFailureCount;
    if (reportedSoFar >= this.initialPeersCount) {
      this.joinSummaryReported = true;
      let howManySeenOnJoin = 'none';
      if (this.initialPeersCount === 0) {
        howManySeenOnJoin = 'first';
      } else if (this.peerSuccessCount === this.initialPeersCount) {
        howManySeenOnJoin = 'all';
      } else if (this.peerSuccessCount > 0) {
        howManySeenOnJoin = 'some';
      }

      console.log("Reporting join summary telemetry", { howManySeenOnJoin, classifier: this.classifier });
      this.socket.emit("joinSummaryTelemetry", {
        how_many_seen_on_join: howManySeenOnJoin,
        classifier: this.classifier
      });
    }
  }

  _reportLeaveSummary() {
    let howManySeenOnLeave = 'none';
    const totalPeers = this.peerSuccessCount + this.peerFailureCount;

    if (this.peerSuccessCount === 0) {
      howManySeenOnLeave = (this.initialPeersCount === 0) ? 'first' : 'none';
    } else if (this.peerSuccessCount >= totalPeers && totalPeers > 0) {
      howManySeenOnLeave = 'all';
    } else if (this.peerSuccessCount > 0) {
      howManySeenOnLeave = 'some';
    }

    console.log("Reporting leave summary telemetry", { howManySeenOnLeave, peerSuccessCount: this.peerSuccessCount, totalPeers, classifier: this.classifier });
    this.socket.emit("leaveSummaryTelemetry", {
      how_many_seen_on_leave: howManySeenOnLeave,
      classifier: this.classifier
    });
  }

  startFakeDrift() {
    console.log("startFakeDrift");
    this.driftingScore = new DriftingScore();
  }

  onSanitizedVideoStreamQualityIncrease(context, newSpatialLayer, score) {
    const { producer, assignedId } = context;

    const qualityDirection = true;

    this.updateLayerQuality(newSpatialLayer, producer, qualityDirection, assignedId, score);
  }

  onSanitizedVideoStreamQualityDecrease(context, newSpatialLayer, score) {
    const { producer, assignedId } = context;

    const qualityDirection = false;

    this.updateLayerQuality(newSpatialLayer, producer, qualityDirection, assignedId, score);
  }

  updateLayerQuality(newSpatialLayer, producer, qualityDirection, assignedId, score) {
    console.log("updateLayerQuality: newSpatialLayer qualityDirection", newSpatialLayer, qualityDirection);
    let maxSpatialLayer = producer.maxSpatialLayer;
    if (maxSpatialLayer === undefined) {
      console.log('current maxSpatialLayer is undefined');
    }


    // Only proceed if the spatial layer is actually changing
    if (maxSpatialLayer !== newSpatialLayer) {
      console.log(`changing maxSpatialLayer from ${maxSpatialLayer} to ${newSpatialLayer}`);
      producer.setMaxSpatialLayer(newSpatialLayer);

      if (qualityDirection === undefined) {
        qualityDirection = null;
      }
      if (newSpatialLayer === undefined) {
        newSpatialLayer = null;
      }
      if (maxSpatialLayer === undefined) {
        maxSpatialLayer = null;
      }
      // Send quality cap change notification
      const data = {
        assignedId,
        newSpatialLayer,
        previousSpatialLayer: maxSpatialLayer,
        qualityDirection: qualityDirection, // true = improved, false = degraded, null = first time
        score: score
      };
      console.log("data", data);
      this.callback({
        type: "streamQualityCapChanged",
        data: data
      });
    }
    else {
      console.log("updateLayerQuality: noop - newSpatialLayer is the same as maxSpatialLayer");
    }
  }

  consumerScoreHandler({ score, assignedId }) {
    // if (score < 2) {
    //   this.socket.emit("toggleIncomingMedia", {
    //     type: "pause",
    //     kind: "video",
    //     assignedId
    //   });
    // } else {
    //   this.socket.emit("toggleIncomingMedia", {
    //     type: "resume",
    //     kind: "video",
    //     assignedId
    //   });
    // }
  }

  producerScoreHandler({ scores, assignedId }) {
    let score = scores.reduce(function (avg, { score }, _, { length }) {
      return avg + score / length;
    }, 0);
    this.callback({
      type: "producerScore",
      data: {
        assignedId,
        score
      }
    })
    const mediaProducer = this.mediaProducers.find(x => x.assignedId === assignedId);
    const producer = mediaProducer.producer;
    if (producer) {
      let newSpatialLayer;
      let newSpatialLayerDescription;
      if (score < 4) {
        newSpatialLayer = 1;
        newSpatialLayerDescription = "lowest";
      } else if (score < 9) {
        newSpatialLayer = 2;
        newSpatialLayerDescription = "medium";
      } else {
        newSpatialLayer = 3;
        newSpatialLayerDescription = "highest";
      }
      console.log(`VideoStreamQualityChangeRequested: new layer quality = ${newSpatialLayerDescription} (${newSpatialLayer})`);
      this.manager.VideoStreamQualityChangeRequested({ assignedId, producer }, newSpatialLayer, score);
    }
  }

  _adaptiveEnabled() {
    return config.options.adaptive
  }

  _getIceServers() {
    return config.options.dontuseturn ? [] : [{
      username: "",
      password: "",
      urls: `stun:${stunServerUrl}`
    }, {
      username: this.turnCreds.username,
      credential: this.turnCreds.password,
      urls: `turn:${turnServerUrl}`
    }]

  }

  sendLayoutToRecorder(props) {
    this.socket.emit("intimateRecordingPeer", props)
  }

  pauseConsumer({ assignedId }) {
    this.mediaConsumers.map(mediaConsumer => {
      if (mediaConsumer.assignedId === assignedId) {
        mediaConsumer.consumer.pause();
      }
    })
  }

  resumeConsumer({ assignedId }) {
    this.mediaConsumers.map(mediaConsumer => {
      if (mediaConsumer.assignedId === assignedId) {
        mediaConsumer.consumer.resume();
      }
    })
  }

  pauseAllConsumers() {
    let mapping = {};
    console.log(`[communicationAdapter] pauseAllConsumers called. Current mediaConsumers count: ${this.mediaConsumers.length}`);
    this.mediaConsumers.forEach(mediaConsumer => {
      if (mediaConsumer.consumer && mediaConsumer.consumer.kind === 'video') {
        console.log(`[communicationAdapter] Pausing video consumer: ${mediaConsumer.assignedId}`);
        mediaConsumer.consumer.pause();
        if (mediaConsumer.consumer.track) {
          mediaConsumer.consumer.track.enabled = false;
        }
        mapping[mediaConsumer.assignedId] = 0;
      }
    })
    if (this.webrtcType === "sfu") {
      this.socket.emit("toggleIncomingMedia", {
        type: "pause",
        kind: "video"
      })
    }
    this.consolidateIncomingVideos(mapping);
  }

  resumeAllConsumers() {
    let mapping = {};
    console.log(`[communicationAdapter] resumeAllConsumers called. Current mediaConsumers count: ${this.mediaConsumers.length}`);
    this.mediaConsumers.forEach(mediaConsumer => {
      if (mediaConsumer.consumer && mediaConsumer.consumer.kind === 'video') {
        console.log(`[communicationAdapter] Resuming video consumer: ${mediaConsumer.assignedId}`);
        mediaConsumer.consumer.resume();
        if (mediaConsumer.consumer.track) {
          mediaConsumer.consumer.track.enabled = true;
        }
        mapping[mediaConsumer.assignedId] = 2; // Default to high
      }
    })
    if (this.webrtcType === "sfu") {
      this.socket.emit("toggleIncomingMedia", {
        type: "resume",
        kind: "video"
      })
    }
    this.consolidateIncomingVideos(mapping);
  }

  disconnect() {
    this._reportLeaveSummary();
    this?.p2pAdapter?.destroy();

    // Give a small grace period for the telemetry leave event to be sent before closing the socket.
    setTimeout(() => {
      this.socket.disconnect();
    }, 500);

    if (this.producerTransport)
      this.producerTransport.close();
    if (this.consumerTransport)
      this.consumerTransport.close();
    this.producerTransport = null;
    this.consumerTransport = null;
  }

  forceReconnect() {
    console.log('Forcing socket reconnection...');
    this.socket.disconnect();
    this.socket.connect();
  }

  emit(msg, socketId) {
    this.socket.emit("metaEvent", {
      ...msg,
      to: socketId
    })
  }

  muteUser(socketId) {
    this.emit({
      type: "muteUser"
    }, socketId)
  }

  muteToggled(isMuted) {
    this.socket.emit("toggleAudioMute", {
      audioMuted: isMuted
    });
  };

  connectedToPeer(path, connectionType, data) {
    this.telemetry({
      type: "event",
      path: path,
      name: "connected_to_peer",
      data: {
        connectionType,
        ...data
      }
    })

    // Prepare conclusion for session summary
    const conclusion = {
      transport: path,
      status: 'established',
      p2p: connectionType === 'P2P',
      turn: connectionType === 'TURN',
      protocol: data?.protocol || 'unknown',
      localCandidateType: data?.localCandidateType,
      remoteCandidateType: data?.remoteCandidateType,
      remoteIp: data?.remoteIp,
      iceState: data?.iceState,
      localUrl: data?.localUrl,
      localPort: data?.localPort,
      localRelatedPort: data?.localRelatedPort,
      localRelatedAddress: data?.localRelatedAddress,
      rtt: data?.rtt
    };

    // Report peer telemetry immediately as soon as we detect 'established'
    this._reportPeerTelemetry(data?.peerId || path, conclusion);
  };

  couldNotConnectToPeer(path, error, data) {
    this.telemetry({
      type: "event",
      path: path,
      name: "could_not_connect_to_peer",
      data: {
        room: this.room,
        error: error,
        ...data
      }
    })

    const conclusion = {
      transport: path,
      status: 'failed',
      error: error
    };

    this._reportPeerTelemetry(data?.peerId || path, conclusion);
  };

  telemetry(data) {
    // Should look something like:
    // data = {
    //   "type": "event",
    //   "name": "connected_to_peer",
    //   "data": {
    //     "peerId": "123",
    //   }
    // data = {
    //   "type": "event",
    //   "name": "could_not_connect_to_peer",
    //   "data": {
    //     "peerId": "123",
    //     "error": "error message"
    //   }
    this.socket.emit("telemetry", data);
  }

  muteAll() {
    this.emit({
      type: "muteUser"
    })
  };


  async identityReceived(data) {
    this.id = data.id;
    if (data.forceSfu) {
      this.webrtcType = "sfu";
    }
    this.callback({
      type: "setname",
      data: {
        name: data.name
      }
    })

    this.callback({
      type: "identity",
      data: {
        id: data.id,
        broadcaster: data.broadcaster,
        peers: data.peers
      }
    })

    this.initialPeersCount = data.peers ? data.peers.length : 0;
    this._reportJoinTelemetry(this.initialPeersCount, data.sessionAge || 0);

    // Set up timeouts for all initial peers (mostly for P2P)
    if (data.peers) {
      data.peers.forEach(({ peerId }) => {
        this._setupPeerTelemetryTimeout(peerId, 'rtc');
      });
    }

    // If we are alone, check join summary immediately as no peer telemetry will follow
    if (this.initialPeersCount === 0) {
      this._checkAndReportJoinSummary();
    }

    console.log("GOT TURN CREDS ", data.turnCreds)
    this.turnCreds = data.turnCreds;
    if (this.webrtcType === "p2p") {
      this.p2pAdapter.identityReceived(data, this._getIceServers());
      return;
    }

    try {
      await this.webRTCDevice.load({
        routerRtpCapabilities: data.roomRTPCapabilities
      });
    } catch (e) {
      console.log(e);
      console.log(data.roomRTPCapabilities);
    }
    this.socket.emit("createTransports", {
      numStreams: this.webRTCDevice.sctpCapabilities.numStreams,
      rtpCapabilities: this.webRTCDevice.rtpCapabilities
    })
  }

  async createdTransports(transports) {
    const turnServer = this._getIceServers().find(s => s.urls.startsWith('turn:'));
    let loggableTurnServer = null;
    if (turnServer) {
      loggableTurnServer = { ...turnServer };
      delete loggableTurnServer.username;
      delete loggableTurnServer.credential;
    }
    console.log("WebRTC connection establishment process started. Turn server:", loggableTurnServer || "not configured");
    console.log('_DEBUG_ transports', transports);
    console.log('_DEBUG_ _getIceServers', this._getIceServers());
    this.manager.StreamStarted(20, null);

    const producerTransportOptions = {
      id: transports.producer.id,
      iceParameters: transports.producer.iceParameters,
      iceCandidates: transports.producer.iceCandidates,
      dtlsParameters: transports.producer.dtlsParameters,
      sctpParameters: transports.producer.sctpParameters,
      iceServers: this._getIceServers(),
    };

    if (process.env.REACT_APP_ICE_TRANSPORT_POLICY) {
      producerTransportOptions.iceTransportPolicy = process.env.REACT_APP_ICE_TRANSPORT_POLICY;
    }

    this.producerTransport = await this.webRTCDevice.createSendTransport(producerTransportOptions);
    this.consumerTransport = await this.webRTCDevice.createRecvTransport(
      {
        id: transports.consumer.id,
        iceParameters: transports.consumer.iceParameters,
        iceCandidates: transports.consumer.iceCandidates,
        dtlsParameters: transports.consumer.dtlsParameters,
        sctpParameters: transports.consumer.sctpParameters,
        iceServers: this._getIceServers()
      });

    // Drain pending consume requests
    if (this.pendingConsumeRequests.length > 0) {
      console.log(`[WebRTC-Tele] Draining ${this.pendingConsumeRequests.length} pending consume requests`);
      const buffer = [...this.pendingConsumeRequests];
      this.pendingConsumeRequests = [];
      this.consumeMedia(buffer);
    }

    // Set up timeouts for SFU transports
    this._setupPeerTelemetryTimeout('producer', 'producer');
    this._setupPeerTelemetryTimeout('consumer', 'consumer');

    const logConnectionDetails = async (transport, transportName) => {

      const stats = await transport.getStats();
      let selectedCandidatePair;
      stats.forEach(report => {
        if (report.type === 'candidate-pair') {
          if (report.state === 'succeeded' || report.nominated === true) {
            if (!selectedCandidatePair || report.nominated === true) {
              selectedCandidatePair = report;
            }
          }
        }
      });

      if (selectedCandidatePair) {
        const localCandidate = stats.get(selectedCandidatePair.localCandidateId);
        const remoteCandidate = stats.get(selectedCandidatePair.remoteCandidateId);

        if (localCandidate && remoteCandidate) {
          const isTurn = localCandidate.candidateType === 'relay' || remoteCandidate.candidateType === 'relay';
          const connectionType = isTurn ? 'TURN' : 'P2P';
          const protocol = (localCandidate.relayProtocol || localCandidate.protocol || 'unknown').toLowerCase();
          console.log(`[WebRTC-Tele] ${transportName}: detected connection details: ${connectionType} protocol: ${protocol}`);
          const conclusion = {
            transport: transportName,
            status: 'established',
            p2p: !isTurn,
            turn: isTurn,
            localCandidateType: localCandidate.candidateType,
            remoteCandidateType: remoteCandidate.candidateType,
            remoteIp: remoteCandidate.ip || remoteCandidate.address || 'unknown',
            protocol: protocol,
            iceState: transport.iceGatheringState,
            localUrl: localCandidate.url || '',
            localPort: localCandidate.port,
            localRelatedPort: localCandidate.relatedPort,
            localRelatedAddress: localCandidate.relatedAddress,
            rtt: selectedCandidatePair.currentRoundTripTime
          };
          this.peerConnected = true;
          if (!this.reportedTransports.has(transportName)) {
            this.reportedTransports.add(transportName);
            console.log(`reporting connected to peer (${transportName})`)
            this.connectedToPeer(transportName, connectionType, conclusion);
            return;
          }

        }
      }
    };

    this.producerTransport.on('connectionstatechange', (state) => {
      console.log(`Producer transport connection state changed to: ${state}`);
      if (state === 'connected') {
        logConnectionDetails(this.producerTransport, 'producer');
      } else if (state === 'failed' || state === 'disconnected') {
        console.warn(`[WebRTC-Tele] Producer transport entered ${state} state. Reporting failure.`);
        this.couldNotConnectToPeer('producer', `transport_${state}`, { transport: 'producer' });
      }
    });

    this.consumerTransport.on('connectionstatechange', (state) => {
      console.log(`Consumer transport connection state changed to: ${state}`);
      if (state === 'connected') {
        logConnectionDetails(this.consumerTransport, 'consumer');
      } else if (state === 'failed' || state === 'disconnected') {
        console.warn(`[WebRTC-Tele] Consumer transport entered ${state} state. Reporting failure.`);
        this.couldNotConnectToPeer('consumer', `transport_${state}`, { transport: 'consumer' });
      }
    });

    const self = this;
    this.consumerTransport.on('connect', async function ({ dtlsParameters }, callback, errback) {
      console.log('consumerTransport connect');
      self.socket.emit('connectTransport', { direction: self.consumerTransport.direction, dtlsParameters: dtlsParameters })
      callback();
    })

    this.producerTransport.on('connect', async function ({ dtlsParameters }, callback, errback) {
      console.log('producerTransport connect');
      self.socket.emit('connectTransport', { direction: self.producerTransport.direction, dtlsParameters: dtlsParameters })
      callback();
    })

    this.producerTransport.on(
      "producedata",
      async (
        { sctpStreamParameters, label, protocol, appData },
        callback,
        errback) => {
        // Send dataProducer data to server
        self.socket.emit('produceData', {
          producerOptions: {
            sctpStreamParameters, label, protocol, appData
          }
        }, function (params) {
          callback({ id: params.id });
        });
      }
    );
    this.producerTransport.on('produce', async function ({ kind, rtpParameters, appData }, callback, errback) {
      //Send producer data to server
      self.socket.emit('produceMedia', {
        producerOptions: {
          kind: kind,
          rtpParameters: rtpParameters,
          appData: appData
        }
      }, function (params) {
        self.mediaProducers = self.mediaProducers.map(producerProps => {
          if (producerProps.assignedId === params.assignedId) {
            return {
              ...producerProps,
              id: params.id
            }
          }
          return producerProps;
        })
        callback({ id: params.id });
      })
    })

    if (transports.botDataChannel) {
      self.consumerTransport.consumeData(transports.botDataChannel)
        .then((newConsumer) => {
          this.botDataChannel = newConsumer;
          this.botDataChannel.on("message", data => {
            this.callback({
              type: "botmessage",
              data: data
            })
          });
        })
    }
    if (this.producerParams.audio) {
    } else {
      const self = this;
      self.muteToggled(true);
    }
    this.createBotDatachannel();
    this.callback({
      type: "transportsCreated"
    })
  }

  async createBotDatachannel() {
    const producer = await this.producerTransport.produceData({
      ordered: false,
      maxRetransmits: 0,
      label: "bot",
      appData: { source: "bot" }
    });
    this.botDataProducer = producer;
  }

  consolidateIncomingVideos(assignedIdToLayerMapping) {
    if (!this.simulcast) {
      return;
    }
    console.log('socket.emit(changeLayerConfiguration)', assignedIdToLayerMapping);
    this.socket.emit("changeLayerConfiguration", {
      assignedIdToLayerMapping
    })
  }

  sendViaBotDataChannel(msg) {
    if (!this.botDataProducer || this.botDataProducer.readyState !== "open") {
      console.warn("Data not sent, datachannel not open yet");
      return;
    }
    if (!msg || msg.length === 0) {
      console.warn("No data to send");
      return;
    }
    this.botDataProducer.send(msg)
  }

  destroyMediaProducer({ assignedId }) {
    const mediaProducer = this.mediaProducers.find(x => x.assignedId === assignedId)
    if (mediaProducer && mediaProducer.producer) {
      try {
        mediaProducer.producer.close();
      } catch (e) {
        console.warn("Retrying closing producer", e);
      }
    }
    this.socket.emit("destroyMedia", {
      assignedId
    })
    // if (mediaProducer.isDesktop) {
    //   this.normalizeOutgoingVideos();
    // }
    this.mediaProducers = this.mediaProducers.filter(x => x.assignedId !== assignedId);
    this.consolidateOutgoingVideoResolutions()
    if (this.videoElProps[assignedId]) {
      this.videoElProps[assignedId].videoEl.srcObject.getTracks().map(t => t.stop());
      delete this.videoElProps[assignedId]
      const self = this;
      Object.keys(this.videoElProps).map(x => {
        self.mainVideoEl = this.videoElProps[x].videoEl;
      })
    }

    this.callback({
      type: "destroyMedia",
      data: {
        assignedId
      }
    })
  }
  getStreamCount() {
    let ougoingVideoStreamCount = this.mediaProducers.filter(x => x.producer.kind === "video").length
    let incomingVideoStreamCount = this.mediaConsumers.filter(x => x.consumer.kind === "video").length
    const count = (ougoingVideoStreamCount + incomingVideoStreamCount)
    return count;
  }
  async consolidateOutgoingVideoResolutions() {
    let count = this.getStreamCount();
    if (this.mediaConsumers.find(x => x.isDesktop) || this.mediaProducers.find(x => x.isDesktop)) {
      // a desktop video is being shared so dont change resolutions
      return;
    }
    if (peerToWidthMapping[count]) {
      this.downgradeOutgoingVideos(peerToWidthMapping[count])
    } else {
      this.downgradeOutgoingVideos(null, 1)
    }
  }
  downgradeOutgoingVideos(desiredWidth = 300, desiredRatio) {
    Object.keys(this.videoElProps).map(assignedId => {
      let { type } = this.videoElProps[assignedId];
      if (type === "desktop") {
        // dont downgrade streaming screen video
        return;
      }
      this.downgradeOutgoingVideo(assignedId, desiredWidth, desiredRatio)
    })
  }
  downgradeOutgoingVideo(assignedId, desiredWidth = 300, ratio) {
    if (!this._adaptiveEnabled()) {
      return;
    }
    let { constraints } = this.videoElProps[assignedId];
    ratio = ratio || (constraints?.video?.width || 640) / desiredWidth;
    let count = this.getStreamCount();
    this.updateRtpParams(assignedId, {
      scaleResolutionDownBy: Math.max(ratio, 1),
      maxBitrate: 1000 * 1000 * (peerToBitrateMapping[count] || 1)
    })
  }
  normalizeOutgoingVideos() {
    Object.keys(this.videoElProps).map(assignedId => {
      let count = this.getStreamCount();
      this.updateRtpParams(assignedId, {
        scaleResolutionDownBy: 1,
        maxBitrate: 1000 * 1000 * (peerToBitrateMapping[count] || 1)
      })
    })
  }
  updateRtpParams(assignedId, params) {
    if (this.webrtcType === "p2p") {
      this.p2pAdapter.updateRtpParams(assignedId, params);
    } else {
      this.mediaProducers.map(mp => {
        if (mp.assignedId === assignedId) {
          mp.producer.setRtpEncodingParameters(params);
        }
        return ""
      })
    }
  }
  replaceTrack({ assignedId, track, constraints, stream, isAudio }) {
    if (this.mainVideoEl) {
      if (!isAudio)
        this.mainVideoEl.srcObject.getTracks().forEach(t => t.stop());
      if (this.webrtcType === "p2p") {
        if (!isAudio)
          this.mainVideoEl.srcObject.addTrack(track);// so that this track gets stopped next time the user changes their device
        this.p2pAdapter.replaceTrack({ assignedId, track, constraints, stream })
      } else {
        this.mediaProducers.map(mp => {
          if (mp.assignedId === assignedId) {
            mp.producer.replaceTrack({ track });
          }
          return ""
        })
      }
    }
    if (!isAudio && this.videoElProps[assignedId] && constraints) {
      this.videoElProps[assignedId].constraints = {
        video: {
          ...this.videoElProps[assignedId].constraints.video,
          ...constraints
        }
      }
      this.videoElProps[assignedId].videoEl = { srcObject: stream }
    }
    this.callback({
      type: "replacedLocalTrack",
      data: {
        assignedId,
        track,
        stream
      }
    })
  }
  changeAspectRatio({ assignedId, ratio }) {
    if (this.videoElProps[assignedId] && this.videoElProps[assignedId].constraints) {
      this.videoElProps[assignedId].constraints = {
        video: {
          ...this.videoElProps[assignedId].constraints.video,
          ...ASPECT_RATIO[this.videoElProps[assignedId].resolution],
          advanced: [
            { aspectRatio: { exact: ratio } },
          ]
        }
      }
      this.videoElProps[assignedId].ratio = ratio
    }
    this.changeInputVideoProps(assignedId, this.videoElProps[assignedId].constraints);
  }
  changeResolution({ assignedId, resolution }) {
    if (this.videoElProps[assignedId] && this.videoElProps[assignedId].constraints) {
      this.videoElProps[assignedId].constraints = {
        video: {
          ...this.videoElProps[assignedId].constraints.video,
          ...ASPECT_RATIO[resolution],
          advanced: [{ aspectRatio: { exact: 1.667 } }]
        }
      }
      this.videoElProps[assignedId].resolution = resolution
    }
    this.changeInputVideoProps(assignedId, this.videoElProps[assignedId].constraints);
    this.callback({
      type: "updateLocalStream",
      data: {
        resolution,
        assignedId
      }
    })
  }
  changeInputVideoProps(assignedId, constraints) {
    const streamOld = this.videoElProps[assignedId].videoEl.srcObject
    streamOld.getTracks()[0].applyConstraints(constraints.video)
      .then(() => {
        this.videoElProps[assignedId].srcObject = streamOld;
      })
  }
  pauseMediaProducer({ assignedId }) {
    console.log(`[communicationAdapter] pauseMediaProducer called for ${assignedId}. Total producers: ${this.mediaProducers.length}`);
    this.mediaProducers.forEach(async (mp) => {
      if (mp.assignedId === assignedId) {
        console.log(`[communicationAdapter] Pausing producer: ${assignedId}, kind: ${mp.producer.kind}`);
        
        // 1. Pause at SFU level
        mp.producer.pause();
        
        // 2. Stop physical traffic at Browser level
        if (mp.producer.track) {
          mp.producer.track.enabled = false;
          // TRICK: replaceTrack(null) is the only way to TRULY stop RTP packets in some browsers
          try {
            await mp.producer.replaceTrack({ track: null });
            console.log(`[communicationAdapter] replaceTrack(null) successful for producer: ${assignedId}`);
          } catch (e) {
            console.warn(`[communicationAdapter] replaceTrack(null) failed for producer: ${assignedId}`, e);
          }
        }

        if (this.webrtcType === "sfu") {
          this.socket.emit("toggleIncomingMedia", {
            type: "pause",
            kind: mp.producer.kind,
            assignedId
          });
        }
      }
    })
    if (this.webrtcType === "p2p") {
      console.warn("[communicationAdapter] P2P producer pausing not fully implemented in p2pAdapter yet.");
    }
  }

  resumeMediaProducer({ assignedId }) {
    console.log(`[communicationAdapter] resumeMediaProducer called for ${assignedId}.`);
    this.mediaProducers.forEach(async (mp) => {
      if (mp.assignedId === assignedId) {
        console.log(`[communicationAdapter] Resuming producer: ${assignedId}, kind: ${mp.producer.kind}`);
        
        // 1. Restore physical traffic
        if (mp.track) { // We keep the track reference in mp.track
          try {
            await mp.producer.replaceTrack({ track: mp.track });
            mp.track.enabled = true;
            console.log(`[communicationAdapter] replaceTrack(original) successful for producer: ${assignedId}`);
          } catch (e) {
            console.warn(`[communicationAdapter] replaceTrack(original) failed for producer: ${assignedId}`, e);
          }
        }

        // 2. Resume at SFU level
        mp.producer.resume();

        if (this.webrtcType === "sfu") {
          this.socket.emit("toggleIncomingMedia", {
            type: "resume",
            kind: mp.producer.kind,
            assignedId
          });
        }
      }
    })
  }
  createAudioProducer(constraints) {
    this.audioTransmitting = true;
    navigator.mediaDevices.getUserMedia(constraints)
      .then(async stream => {
        const assignedId = uuid();
        const producer = await this.producerTransport.produce({ track: stream.getAudioTracks()[0], appData: { assignedId } });
        this.mediaProducers.push({
          assignedId,
          producer
        });
        this.callback({
          type: "audiocreated",
          data: {
            stream,
            assignedId
          }
        })
      })
      .catch(e => {
        this.callback({
          type: "audioDevicePermissionIssue"
        })
      });
  }

  whiteNoise() {
    let canvas = Object.assign(document.createElement("canvas"), { width: 320, height: 240 });
    let ctx = canvas.getContext('2d');
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    let p = ctx.getImageData(0, 0, canvas.width, canvas.height);
    requestAnimationFrame(function draw() {
      for (var i = 0; i < p.data.length; i++) {
        p.data[i++] = p.data[i++] = p.data[i++] = Math.random() * 255;
      }
      ctx.putImageData(p, 0, 0);
      requestAnimationFrame(draw);
    });
    return canvas.captureStream(60);
  }
  toggleBW(assignedId) {
    if (!this.filters[assignedId]) {
      this.filters[assignedId] = {};
    }
    this.filters[assignedId].grayscale = !this.filters[assignedId].grayscale;
    this.callback({
      type: "updateFilter",
      data: {
        ...this.filters[assignedId],
        assignedId
      }
    })
    this.emit({
      type: "updateFilter",
      grayscale: this.filters[assignedId].grayscale,
      assignedId
    })
  }
  toggleBrightness(assignedId, val) {
    if (!this.filters[assignedId]) {
      this.filters[assignedId] = {};
    }
    let brightness = this.filters[assignedId].brightness || 1;
    if (!brightness) {
      brightness = 1;
    }
    brightness += val;

    if (brightness < 1) {
      brightness = 1;
    }
    if (brightness > 4) {
      brightness = 4;
    }
    this.filters[assignedId].brightness = brightness;
    this.callback({
      type: "updateFilter",
      data: {
        ...this.filters[assignedId],
        assignedId
      }
    })
    this.emit({
      type: "updateFilter",
      brightness: this.filters[assignedId].brightness,
      assignedId
    })
  }

  createCanvas(stream, assignedId) {
    const canvas = document.createElement("canvas");
    const videoEl = document.createElement("video");
    // this.mainVideoEl = videoEl;
    videoEl.srcObject = stream;
    videoEl.muted = true;
    // const self = this;
    // this.videoElProps[assignedId].videoEl = videoEl;
    videoEl.addEventListener("resize", () => {
      canvas.setAttribute("width", videoEl.videoWidth);
      canvas.setAttribute("height", videoEl.videoWidth / 1.667);
    })
    document.body.appendChild(videoEl);
    videoEl.setAttribute("style", "display: none");
    videoEl.oncanplay = () => {
      console.log("videoEl.oncanplay");
      let playPromise = videoEl.play();
      playPromise.then(_ => {
        console.log("videoEl.play() resolved");
      }).catch(e => {
        console.log("videoEl.play() rejected", e);
      });
      if (!canvas || !this.videoElProps[assignedId]) {
        return;
      }
      canvas.setAttribute("height", videoEl.videoWidth / 1.667);
      canvas.setAttribute("width", videoEl.videoWidth);
      this.videoElProps[assignedId].canvas = canvas;
      const ctx = canvas.getContext("2d");
      const self = this;
      (function loop() {
        ctx.clearRect(0, 0, videoEl.videoWidth, videoEl.videoHeight);
        ctx.drawImage(videoEl, 0, 0, videoEl.videoWidth, videoEl.videoHeight); // draw the video
        // if (self.filters[assignedId] === BLACK_AND_WHITE) {
        //   ctx.fillStyle = "#888"; // gray colour
        //   ctx.globalAlpha = 1;   // amount of FX
        //   ctx.globalCompositeOperation = "color";  // The comp setting to do BLACK/WHITE
        //   ctx.fillRect(0, 0, videoEl.videoWidth, videoEl.videoHeight);  // Draw gray over the video
        //   ctx.globalAlpha = 1;  // reset alpha
        //   ctx.globalCompositeOperation = "source-over";  // reset comp
        // }
        // if (self.brightness[assignedId]) {
        //   ctx.filter = `brightness(${100 + (self.brightness[assignedId] - 2) * 25}%)`;
        // }
        setTimeout(loop, 1000 / 30); // drawing at 30fps
      })();
    }
    // // document.body.appendChild(videoEl);

    // // document.body.appendChild(canvas)
    return canvas.captureStream(24);
  }

  async createVideoProducer(constraints, stream, resolution, label) {
    resolution = resolution || 1;
    let videoConstraints = {
      ...constraints.video,
      ...ASPECT_RATIO[resolution],
      advanced: [
        { aspectRatio: { exact: 1.6667 } },
      ]
    };
    constraints = {
      ...constraints,
      video: videoConstraints
    }

    if (!stream) {
      let errThrown = false;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints)
      } catch (e) {
        errThrown = true;
        this.callback({
          type: "videoDevicePermissionIssue"
        })
      }
      if (errThrown) {
        return;
      }
    }

    if (!stream) {
      console.error("Permission denied");
      return;
    }

    let assignedId = uuid();
    if (this.webrtcType === "p2p") {
      assignedId = stream.id;
    }
    console.log("createVideoProducer: assignedId", assignedId);
    this.videoElProps[assignedId] = {
      type: "webcam",
      constraints: constraints,
      resolution: resolution,
      label,
      aspectRatio: 1.5,
      videoEl: {
        srcObject: stream
      }
    };
    this.mainVideoEl = {
      srcObject: stream
    };
    // stream = this.createCanvas(stream, assignedId);
    if (this.webrtcType === "p2p") {
      this.p2pAdapter.addVideoStream(stream);
    } else {
      let options = {
        stopTracks: false,
        track: stream.getVideoTracks()[0].clone(),
        appData: { assignedId, label }
      }
      if (this.simulcast) {
        options.encodings = WEBCAM_SIMULCAST_ENCODINGS;
      }
      const producer = await this.producerTransport.produce(options);
      this.mediaProducers.push({
        assignedId,
        producer,
        track: options.track // Store track for replaceTrack
      });
      this.consolidateOutgoingVideoResolutions()
      if (this.mediaConsumers.find(x => x.isDesktop)) {
        // a user is sharing desktop
        this.downgradeOutgoingVideo(assignedId)
      }
    }

    this.callback({
      type: "videocreated",
      data: {
        stream,
        assignedId,
        metadata: this.metadata,
        deviceId: constraints.video?.deviceId?.exact || constraints.video?.deviceId || null,
        resolution: resolution,
        label
      }
    })
  }
  getDisplayStream(constraints) {
    return navigator.mediaDevices.getDisplayMedia(constraints);
  }
  async createDesktopProducer(constraints, stream) {
    if (!stream) {
      try {
        stream = await this.getDisplayStream(constraints);
      } catch (e) {
        console.warn("Screen share cancelled or failed", e);
        return;
      }
    }
    if (!stream) {
      console.error("Access denied while asking for screenshare");
      return;
    }
    let assignedId = uuid();
    if (this.webrtcType === "p2p") {
      assignedId = stream.id
      this.p2pAdapter.addVideoStream(stream, true);
    } else {
      const producer = await this.producerTransport.produce({
        stopTracks: false,
        track: stream.getVideoTracks()[0],
        appData: { assignedId, isDesktop: true }
      })
      this.mediaProducers.push({
        assignedId,
        producer,
        isDesktop: true,
        track: stream.getVideoTracks()[0]
      });
    }
    this.videoElProps[assignedId] = {
      type: "desktop",
      constraints: constraints,
      videoEl: {
        srcObject: stream
      }
    };
    this.downgradeOutgoingVideos();
    this.callback({
      type: "desktopvideocreated",
      data: {
        stream,
        assignedId,
        isDesktop: true
      }
    })
    const self = this;
    stream.getVideoTracks()[0].onended = function () {
      self.destroyMediaProducer({ assignedId })
      self.callback({
        type: "desktopVideoDestroyed"
      })
    };
    ///conn.send(succes)
  }
  async destroyMedia(data) {
    const { assignedId } = data;
    // let mediaConsumer = this.mediaConsumers.find(x => x.assignedId === assignedId);
    // if (mediaConsumer?.isDesktop) {
    //   // this.normalizeOutgoingVideos();
    //   this.consolidateOutgoingVideoResolutions();
    // }
    this.mediaConsumers = this.mediaConsumers.filter(x => x.assignedId !== assignedId);
    this.consolidateOutgoingVideoResolutions();
    this.callback({
      type: "destroyMedia",
      data: {
        assignedId
      }
    })
  }
  async startRecording({ audioTrack, videoTrack }) {
    const assignedId = uuid();
    const videoProducer = await this.producerTransport.produce({
      track: videoTrack,
      appData: { assignedId, isRecording: true }
    })
    const audioProducer = await this.producerTransport.produce({
      track: audioTrack,
      appData: { assignedId, isRecording: true }
    })
  }
  async consumeMedia(data) {
    if (!this.consumerTransport) {
      console.warn("[WebRTC-Tele] Consumer transport not yet initialized. Buffering media request.")
      this.pendingConsumeRequests.push(...data);
      return;
    }
    for (let i = 0; i < data.length; i++) {
      const { peerId, assignedId, consumerOptions, mediaId, isDesktop, audioMuted, metadata, name, user_id, filters, label } = data[i];
      await this.consumerTransport.consume(consumerOptions)
        .then((newConsumer) => {
          const checkMediaFlow = async (retries = 0) => {
            if (this.peerTelemetryReported.has(peerId)) return; // Already reported success elsewhere

            const stats = await newConsumer.getStats();
            let bytesReceived = 0;
            stats.forEach(report => {
              if (report.type === 'inbound-rtp') {
                bytesReceived += report.bytesReceived;
              }
            });

            console.log(`[WebRTC-Tele] consumer: ${newConsumer.id} (peer: ${peerId}) check_retries:${retries} bytes received: ${bytesReceived}`);

            if (bytesReceived > 0) {
              const transportStats = await this.consumerTransport.getStats();
              let selectedCandidatePair;
              transportStats.forEach(report => {
                if (report.type === 'candidate-pair') {
                  if (report.state === 'succeeded' || report.nominated === true) {
                    if (!selectedCandidatePair || report.nominated === true) {
                      selectedCandidatePair = report;
                    }
                  }
                }
              });

              let connectionType = 'unknown';
              let protocol = 'unknown';
              if (selectedCandidatePair) {
                const localCandidate = transportStats.get(selectedCandidatePair.localCandidateId);
                const remoteCandidate = transportStats.get(selectedCandidatePair.remoteCandidateId);
                if (localCandidate && remoteCandidate) {
                  const isTurn = localCandidate.candidateType === 'relay' || remoteCandidate.candidateType === 'relay';
                  connectionType = isTurn ? 'TURN' : 'P2P';
                  protocol = (localCandidate.relayProtocol || localCandidate.protocol || 'unknown').toLowerCase();
                }
              }

              console.log("[WebRTC-Tele] consumer: success detected proactively");
              this.connectedToPeer('consumerTransport', connectionType, {
                peerId: peerId,
                protocol: protocol
              });
              return;
            }

            if (retries < 15) { // Poll for ~30-45 seconds total in increasing increments
              const nextWait = Math.min(2000 + (retries * 1000), 10000);
              setTimeout(() => checkMediaFlow(retries + 1), nextWait);
            }
          };

          checkMediaFlow();

          this.mediaConsumers.push({
            assignedId,
            consumer: newConsumer,
            peerId,
            name,
            isDesktop
          })
          this.consolidateOutgoingVideoResolutions()
          if (isDesktop) {
            this.downgradeOutgoingVideos();
          }
          this.callback({
            type: "consumemedia", data: {
              track: newConsumer.track, peerId, assignedId, mediaId, isDesktop, audioMuted, metadata, name, user_id, filters, label
            }
          })
        })
        .catch(async error => {
          const stats = await this.consumerTransport.getStats();
          let selectedCandidatePair;
          stats.forEach(report => {
            if (report.type === 'candidate-pair' && report.state === 'succeeded') {
              selectedCandidatePair = report;
            }
          });
          let connectionType = 'unknown';
          if (selectedCandidatePair) {
            const localCandidate = stats.get(selectedCandidatePair.localCandidateId);
            const remoteCandidate = stats.get(selectedCandidatePair.remoteCandidateId);
            if (localCandidate && remoteCandidate) {
              const isTurn = localCandidate.candidateType === 'relay' || remoteCandidate.candidateType === 'relay';
              connectionType = isTurn ? 'TURN' : 'P2P';
            }
          }
          if (window.adapter && window.adapter.couldNotConnectToPeer) {
            console.log("consumerTransport: couldNotConnectToPeer", error.toString())
            this.couldNotConnectToPeer('consumerTransport', error.toString(), {
              roomId: this.room,
              peerId: peerId,
              producerId: consumerOptions.producerId,
              connectionType
            });
          }
          console.error('Failed to consume media', error);
        })
    }
  }
  async getOutgoingBandwidth() {
    if (!this.producerTransport) {
      return {};
    }
    const stats = await this.producerTransport.getStats();
    let audioBytes = 0;
    let videoBytes = 0;
    stats.forEach(r => {
      if (r.type.includes("outbound-rtp")) {
        if (r.kind === "audio") {
          audioBytes += r.bytesSent;
        } else {
          videoBytes += r.bytesSent;
        }
      }
    })
    return {
      videoBytes,
      audioBytes
    }
  }
  async getPeerStats(peerName) {
    const stats = {
      sending: false,
      receiving: false,
      bytesSent: 0,
      bytesReceived: 0
    };

    if (peerName === 'You' || peerName === 'local') {
      if (this.producerTransport) {
        const s = await this.producerTransport.getStats();
        s.forEach(r => {
          if (r.type === 'outbound-rtp' && r.kind === 'video') {
            stats.bytesSent += r.bytesSent;
          }
        });
        stats.sending = stats.bytesSent > 0;
      }
      return stats;
    }

    const consumerObj = this.mediaConsumers.find(c => c.name === peerName && c.consumer.kind === 'video');
    if (consumerObj && consumerObj.consumer) {
      const s = await consumerObj.consumer.getStats();
      s.forEach(r => {
        if (r.type === 'inbound-rtp' && r.kind === 'video') {
          stats.bytesReceived += r.bytesReceived;
        }
      });
      stats.receiving = stats.bytesReceived > 0;
    }

    return stats;
  }

  async getIncomingBandwidth() {
    if (!this.consumerTransport) {
      return {};
    }
    const stats = await this.consumerTransport.getStats();
    let audioBytes = 0;
    let videoBytes = 0;
    stats.forEach(r => {
      if (r.type.includes("inbound-rtp")) {
        if (r.kind === "audio") {
          audioBytes += r.bytesReceived;
        } else {
          videoBytes += r.bytesReceived;
        }
      }
    })
    return {
      videoBytes,
      audioBytes
    }
  }
}
